import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import GoogleMap from '../components/GoogleMap'
import { getResizedImageUrl } from '../lib/utils'

export default function Kontakt() {
  const [team, setTeam] = useState([])
  const [loading, setLoading] = useState(true)

  // Form State
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [formError, setFormError] = useState('')
  const [formLoading, setFormLoading] = useState(false)

  useEffect(() => {
    fetch('/_api/public/content/medarbejdere')
      .catch(() => fetch('https://api.dev.auxo.dk/public/content/medarbejdere'))
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setTeam(data)
        }
        setLoading(false)
      })
      .catch((err) => {
        console.error('Fejl ved hentning af medarbejdere:', err)
        setLoading(false)
      })
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setFormLoading(true)
    setFormError('')

    const formData = new FormData(e.target)
    const data = Object.fromEntries(formData.entries())

    // Validate fields
    if (!data.navn || !data.phone || !data.email || !data.beskrivelse) {
      setFormError('Udfyld venligst alle påkrævede felter.')
      setFormLoading(false)
      return
    }

    try {
      const response = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      })

      if (response.ok) {
        setFormSubmitted(true)
      } else {
        setFormError('Der opstod en fejl ved afsendelse af formularen. Prøv igen senere.')
      }
    } catch (err) {
      console.error(err)
      setFormError('Netværksfejl. Kontroller din internetforbindelse og prøv igen.')
    } finally {
      setFormLoading(false)
    }
  }

  return (
    <>
      <Helmet>
        <title>Kontakt | Recars ApS</title>
        <meta name="description" content="Har du spørgsmål eller kommentarer? Kontakt os i dag for at høre mere om, hvad vi kan gøre for dig." />
        <meta property="og:title" content="Kontakt | Recars ApS" />
        <meta property="og:description" content="Har du spørgsmål eller kommentarer? Kontakt os i dag for at høre mere om, hvad vi kan gøre for dig." />
        <link rel="canonical" href="https://recars.dk/kontakt/" />
      </Helmet>

      {/* Header */}
      <section className="relative bg-[#F5F2EB] pt-32 sm:pt-40 lg:pt-44 pb-16 border-b border-stone-300">
        <div className="mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-8 xl:px-12 text-left">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9b784a]">
            Vi glæder os til at høre fra dig!
          </span>
          <h1 className="mt-2 text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl font-display">
            Kontakt os i dag
          </h1>
          <p className="mt-4 max-w-2xl text-sm text-neutral-600">
            Har du spørgsmål eller kommentarer? Kontakt os i dag for at høre mere om, hvad vi kan gøre for dig.
          </p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="bg-[#FAF9F6] py-20">
        <div className="mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left Column: Contact Info */}
            <div className="lg:col-span-5 text-left flex flex-col gap-8">
              <div>
                <h2 className="max-w-full break-words text-xl font-bold uppercase tracking-wide text-neutral-900 sm:text-2xl sm:tracking-wider font-display">
                  Vores kontaktinformationer
                </h2>
                <p className="mt-2 text-xs text-neutral-600">
                  Du er altid velkommen til at ringe, sende en mail eller kigge forbi vores bilhus i Børkop.
                </p>
              </div>

              <div className="rounded-2xl border border-stone-200 bg-white p-6 md:p-8 flex flex-col gap-6 shadow-md">
                {/* Address */}
                <div className="flex gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#DFB587]/20 border border-[#DFB587]/40 text-[#9b784a]">
                    <i className="fa-solid fa-location-dot" />
                  </div>
                  <div>
                    <h4 className="font-display text-xs font-bold text-neutral-900 uppercase tracking-wider">Adresse</h4>
                    <p className="text-sm text-neutral-700 mt-1">
                      Recars ApS<br />
                      <a
                        href="https://www.google.com/maps/place/Industrivej%2052,%207080%20B%C3%B8rkop"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-[#9b784a] transition-colors"
                      >
                        Industrivej 52, 7080 Børkop
                      </a>
                    </p>
                  </div>
                </div>

                {/* Email */}
                <div className="flex gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#DFB587]/20 border border-[#DFB587]/40 text-[#9b784a]">
                    <i className="fa-solid fa-envelope" />
                  </div>
                  <div>
                    <h4 className="font-display text-xs font-bold text-neutral-900 uppercase tracking-wider">E-mail</h4>
                    <p className="text-sm text-neutral-700 mt-1">
                      <a href="mailto:info@recars.dk" className="hover:text-[#9b784a] transition-colors whitespace-nowrap">
                        info@recars.dk
                      </a>
                    </p>
                  </div>
                </div>

                {/* CVR */}
                <div className="flex gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#DFB587]/20 border border-[#DFB587]/40 text-[#9b784a]">
                    <i className="fa-solid fa-file-invoice" />
                  </div>
                  <div>
                    <h4 className="font-display text-xs font-bold text-neutral-900 uppercase tracking-wider">CVR-nummer</h4>
                    <p className="text-sm text-neutral-700 mt-1">
                      CVR nr.: 44385171
                    </p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#DFB587]/20 border border-[#DFB587]/40 text-[#9b784a]">
                    <i className="fa-solid fa-phone" />
                  </div>
                  <div>
                    <h4 className="font-display text-xs font-bold text-neutral-900 uppercase tracking-wider">Hovednummer</h4>
                    <p className="text-sm text-neutral-700 mt-1">
                      <a href="tel:+4571999177" className="hover:text-[#9b784a] transition-colors whitespace-nowrap">
                        +45 71 99 91 77
                      </a>
                    </p>
                  </div>
                </div>
              </div>

              {/* Opening Hours */}
              <div className="rounded-2xl border border-stone-200 bg-white p-6 md:p-8 shadow-md">
                <h4 className="font-display text-xs font-bold text-neutral-900 uppercase tracking-wider border-b border-stone-200 pb-3 mb-4">
                  Åbningstider
                </h4>
                <ul className="space-y-2 text-xs text-neutral-700">
                  <li className="flex justify-between border-b border-stone-100 pb-1.5">
                    <span>Mandag – Fredag:</span>
                    <span className="text-neutral-900 font-medium">9.00 – 16.00</span>
                  </li>
                  <li className="flex justify-between border-b border-stone-100 pb-1.5">
                    <span>Lørdag:</span>
                    <span className="text-neutral-400">Lukket</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Søndag:</span>
                    <span className="text-neutral-900 font-medium">10.00 – 13.00</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Right Column: Contact Form */}
            <div className="lg:col-span-7">
              <div className="rounded-2xl border border-stone-200 bg-white p-6 md:p-8 text-left shadow-lg">
                <h3 className="font-display text-base font-bold uppercase tracking-wider text-neutral-900 border-b border-stone-200 pb-4 mb-6">
                  Udfyld kontaktformularen her
                </h3>

                {formSubmitted ? (
                  <div className="rounded-xl border border-emerald-500/30 bg-emerald-50 p-8 text-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-600 mx-auto mb-4">
                      <i className="fa-solid fa-check text-lg" />
                    </div>
                    <h4 className="font-display text-sm font-bold text-neutral-900 uppercase tracking-wider">Tak for din henvendelse!</h4>
                    <p className="text-xs text-neutral-700 mt-2">
                      Vi har modtaget din besked og vender tilbage til dig så hurtigt som muligt.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                    {/* Antispam Honeypot - ABSOLUTELY HIDDEN */}
                    <div className="sr-only absolute" aria-hidden="true">
                      <label htmlFor="website_url">Do not fill this out</label>
                      <input
                        type="text"
                        id="website_url"
                        name="website_url"
                        tabIndex="-1"
                        autoComplete="off"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      {/* Name */}
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-700">Navn *</label>
                        <input
                          type="text"
                          name="navn"
                          required
                          placeholder="Dit fulde navn"
                          className="w-full rounded-xl border border-stone-300 bg-stone-50 px-4 py-3 text-xs text-neutral-900 placeholder-neutral-400 focus:border-[#DFB587] focus:bg-white focus:outline-none transition-colors"
                        />
                      </div>

                      {/* Phone */}
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-700">Telefonnummer *</label>
                        <input
                          type="tel"
                          name="phone"
                          required
                          placeholder="Dine 8 cifre"
                          className="w-full rounded-xl border border-stone-300 bg-stone-50 px-4 py-3 text-xs text-neutral-900 placeholder-neutral-400 focus:border-[#DFB587] focus:bg-white focus:outline-none transition-colors"
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-700">E-mail *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="din@email.dk"
                        className="w-full rounded-xl border border-stone-300 bg-stone-50 px-4 py-3 text-xs text-neutral-900 placeholder-neutral-400 focus:border-[#DFB587] focus:bg-white focus:outline-none transition-colors"
                      />
                    </div>

                    {/* Message */}
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-700">Besked *</label>
                      <textarea
                        name="beskrivelse"
                        required
                        rows="6"
                        placeholder="Skriv din besked eller dit spørgsmål her..."
                        className="w-full rounded-xl border border-stone-300 bg-stone-50 px-4 py-3 text-xs text-neutral-900 placeholder-neutral-400 focus:border-[#DFB587] focus:bg-white focus:outline-none transition-colors resize-none"
                      />
                    </div>

                    {formError && (
                      <p className="text-xs text-red-600 font-medium">{formError}</p>
                    )}

                    <div>
                      <button
                        type="submit"
                        disabled={formLoading}
                        className="rounded-xl bg-[#DFB587] px-8 py-4 text-xs font-bold uppercase tracking-wider text-neutral-950 hover:bg-[#cf9f6d] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                      >
                        {formLoading ? (
                          <span className="flex items-center gap-2">
                            <i className="fa-solid fa-spinner animate-spin" /> Sender...
                          </span>
                        ) : (
                          'Send besked'
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>

          {/* RECARS Teamet Section */}
          <div className="mt-24 border-t border-stone-200 pt-16">
            <div className="text-left mb-12">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9b784a]">
                Mød vores rådgivere
              </span>
              <h2 className="mt-2 text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl font-display">
                RECARS Teamet
              </h2>
              <p className="mt-4 max-w-2xl text-sm text-neutral-600">
                Vores erfarne team står altid klar til at yde personlig rådgivning og sikre dig en tryg bilhandel.
              </p>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#DFB587] border-t-transparent" />
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                {team.map((member) => {
                  const position = typeof member.stilling === 'object' && member.stilling !== null ? (member.stilling.da || '') : (member.stilling || '')
                  const desc = typeof member.beskrivelse === 'object' && member.beskrivelse !== null ? (member.beskrivelse.da || '') : (member.beskrivelse || '')
                  const imgUrl = member.billede?.[0] || 'https://assets.auxodev.dk/assets/fc45746b-58f8-47db-83c4-feb0973bc76d/medarbejder-placeholder.jpg'
                  
                  // Extract email and phone from description if available
                  const emailMatch = desc ? desc.match(/E-mail:\s*([^\s,]+)/i) : null
                  const phoneMatch = desc ? desc.match(/Tlf:\s*([^\s,]+(?:\s+[^\s,]+)*)/i) : null
                  const email = emailMatch ? emailMatch[1] : ''
                  const phone = phoneMatch ? phoneMatch[1] : ''
                  const cleanDesc = desc ? desc.split('E-mail:')[0].trim() : ''

                  return (
                    <div
                      key={member.id}
                      className="group rounded-2xl border border-stone-200 bg-white overflow-hidden shadow-lg hover:border-[#DFB587] hover:shadow-xl transition-all duration-300"
                    >
                      <div className="relative aspect-square overflow-hidden bg-stone-100">
                        <img
                          src={getResizedImageUrl(imgUrl, 500)}
                          alt={member.navn}
                          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="p-6 text-left">
                        <h3 className="font-display text-base font-bold text-neutral-900 group-hover:text-[#9b784a] transition-colors">
                          {member.navn}
                        </h3>
                        {position && (
                          <p className="text-xs text-[#9b784a] font-semibold uppercase tracking-wider mt-1">
                            {position}
                          </p>
                        )}
                        {cleanDesc && (
                          <p className="text-xs text-neutral-600 mt-3 leading-relaxed">
                            {cleanDesc}
                          </p>
                        )}
                        
                        {/* Contact info for employee */}
                        <div className="mt-4 pt-4 border-t border-stone-100 flex flex-col gap-2 text-xs">
                          {email && (
                            <a href={`mailto:${email}`} className="flex items-center gap-2 text-neutral-700 hover:text-[#9b784a] transition-colors whitespace-nowrap">
                              <i className="fa-regular fa-envelope text-[#9b784a] w-4" />
                              <span>{email}</span>
                            </a>
                          )}
                          {phone && (
                            <a href={`tel:${phone.replace(/\s+/g, '')}`} className="flex items-center gap-2 text-neutral-700 hover:text-[#9b784a] transition-colors whitespace-nowrap">
                              <i className="fa-regular fa-phone text-[#9b784a] w-4" />
                              <span>{phone}</span>
                            </a>
                          )}
                          {member.linkedin && (
                            <a href={member.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-neutral-700 hover:text-[#9b784a] transition-colors whitespace-nowrap">
                              <i className="fa-brands fa-linkedin text-[#9b784a] w-4" />
                              <span>LinkedIn Profil</span>
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Google Maps Section */}
      <GoogleMap />
    </>
  )
}
